
import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Calendar,
  BarChart3,
  Users,
  Dumbbell,
  Clock,
  Settings,
  UserCircle,
  Package,
  Stars,
  Wallet,
  ListChecks,
  CreditCard,
  Sparkles,
  AlertTriangle,
  Landmark
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarGroupLabel,
  useSidebar,
} from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";
import { AppVersionTag } from "@/components/common/AppVersionTag";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTranslation } from "react-i18next";
import { useThemeCustomization } from "@/hooks/useThemeCustomization";
import { useTouchNavigation } from "@/hooks/useTouchNavigation";

interface SidebarNavProps {
  isCoach?: boolean;
}

export function SidebarNav({ isCoach = false }: SidebarNavProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { makePointerDownHandler, onPointerMove, makePointerUpNavigateHandler, onPointerCancel, makeKeyHandler } = useTouchNavigation();
  const { openMobile, setOpenMobile } = useSidebar();
  const { t } = useTranslation(["common", "appui"]);
  const { logoUrl, brandName } = useThemeCustomization();
  
  // Track when sidebar was last opened to prevent instant closure
  const lastOpenedAtRef = useRef<number>(0);
  const prevPathnameRef = useRef<string>(location.pathname);

  // Update lastOpenedAt when sidebar opens
  useEffect(() => {
    if (openMobile) {
      lastOpenedAtRef.current = Date.now();
    }
  }, [openMobile]);

  // Close sidebar on mobile when route changes - with guard against instant closure
  useEffect(() => {
    // Only close if pathname actually changed
    if (prevPathnameRef.current === location.pathname) {
      return;
    }
    prevPathnameRef.current = location.pathname;
    
    if (isMobile && openMobile) {
      // Don't close if sidebar was just opened (within 300ms)
      const timeSinceOpen = Date.now() - lastOpenedAtRef.current;
      if (timeSinceOpen < 300) {
        return;
      }
      
      const timeout = setTimeout(() => {
        setOpenMobile(false);
      }, 100);
      return () => clearTimeout(timeout);
    }
  }, [location.pathname, isMobile, openMobile, setOpenMobile]);

  // All menu items
  const allMenuItems = [
    {
      label: t("dashboard"),
      icon: BarChart3,
      path: "/admin/dashboard",
      showFor: ['admin']
    },
    {
      label: t("dashboard"),
      icon: BarChart3,
      path: "/coach/dashboard",
      showFor: ['coach']
    },
    {
      label: t("clients"),
      icon: Users,
      path: "/admin/clients",
      showFor: ['admin']
    },
    {
      label: t("coaches"),
      icon: UserCircle,
      path: "/admin/coaches",
      showFor: ['admin']
    },
    {
      label: t("classes"),
      icon: Dumbbell,
      path: "/admin/classes",
      showFor: ['admin']
    },
    {
      label: t("schedule"),
      icon: Calendar,
      path: isCoach ? "/coach/schedule" : "/admin/schedule",
      showFor: ['admin', 'coach']
    },
    {
      label: t("inventory"),
      icon: Package,
      path: "/admin/inventory",
      showFor: ['admin']
    },
    {
      label: t("attendance"),
      icon: Clock,
      path: "/admin/attendance",
      showFor: ['admin']
    },
    {
      label: t("payments"),
      icon: Wallet,
      path: "/admin/payments",
      showFor: ['admin']
    },
    {
      label: t("title", { ns: "accounting" }),
      icon: Landmark,
      path: "/admin/accounting",
      showFor: ['admin']
    },
    {
      label: t("ratings"),
      icon: Stars,
      path: "/admin/ratings",
      showFor: ['admin']
    },
    {
      label: t("audit"),
      icon: ListChecks,
      path: "/admin/audit",
      showFor: ['admin']
    },
    {
      label: t("subscriptionTypes"),
      icon: CreditCard,
      path: "/admin/subscription-types",
      showFor: ['admin']
    },
    {
      label: t("sidebar.trialSessions", { ns: "appui" }),
      icon: Sparkles,
      path: "/admin/trials",
      showFor: ['admin']
    },
    {
      // Même page pour le coach, sous son propre path (gating interne).
      label: t("sidebar.trialSessions", { ns: "appui" }),
      icon: Sparkles,
      path: "/coach/trials",
      showFor: ['coach']
    },
    {
      label: t("sidebar.sessionDebts", { ns: "appui" }),
      icon: AlertTriangle,
      path: "/admin/session-debts",
      showFor: ['admin']
    },
    {
      label: t("settings"),
      icon: Settings,
      path: "/admin/settings",
      showFor: ['admin']
    },
    {
      label: t("statistics"),
      icon: Calendar,
      path: "/coach/statistics",
      showFor: ['coach']
    }, 
  ];

  // Filter menu items based on role
  const menuItems = allMenuItems.filter(item => 
    isCoach ? item.showFor.includes('coach') : item.showFor.includes('admin')
  );

  return (
    <Sidebar className="sidebar border-r lg:pt-16">
      <SidebarHeader className="py-6 pt-[calc(env(safe-area-inset-top)+1.5rem)]">
        <div className="flex items-center justify-center">
          <img 
            src={logoUrl}
            alt={`${brandName} Logo`}
            className="h-16 w-auto object-contain" 
          />
        </div>
      </SidebarHeader>
      
      <SidebarContent>
        <SidebarMenu>
          <SidebarGroupLabel>{t("menu")}</SidebarGroupLabel>
          {menuItems.map((item) => (
              <SidebarMenuItem key={item.path}>
                <SidebarMenuButton
                  type="button"
                  draggable={false}
                  data-nav-path={item.path}
                  onPointerDown={makePointerDownHandler(item.path)}
                  onPointerMove={onPointerMove}
                  onPointerUp={makePointerUpNavigateHandler(item.path)}
                  onPointerCancel={onPointerCancel}
                  onKeyDown={makeKeyHandler(item.path)}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(item.path);
                  }}
                  className={cn(
                    "group flex items-center gap-2 transition-all min-h-11 px-2",
                    "touch-manipulation select-none",
                    location.pathname === item.path && "text-primary font-medium"
                  )}
                  role="link"
                  aria-current={location.pathname === item.path ? "page" : undefined}
                  aria-label={item.label}
                >
                <item.icon
                  className={cn(
                    "h-5 w-5",
                    location.pathname === item.path
                      ? "text-primary"
                      : "text-muted-foreground group-hover:text-foreground transition-colors"
                  )}
                />
                <span>{item.label}</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          ))}
        </SidebarMenu>
      </SidebarContent>
      
      <SidebarFooter className="border-t py-2 flex items-center justify-center">
        <AppVersionTag />
      </SidebarFooter>
    </Sidebar>
  );
}
